import ActivityList from "@/components/common/ActivityList";
import { RoleTagPill } from "./RoleTagPill";

type AnyRecord = Record<string, any>;

/**
 * Recent Activity, in the shared portal card treatment. Sits at the bottom of
 * every portal's right rail, below the point-of-contact card.
 *
 * The client portal never rendered this before — every activity row was
 * stamped `shared_with_externals`, which `visibility_filter` hides entirely
 * from a `role === "client"` viewer, so a client's feed was silently empty.
 * Fixed at the source (`log_activity` now stamps a curated `client_visible`
 * tier for task completions, phase advances, and document shares — see
 * `backend/deps.py` / `routes/deals.py`), so this component simply renders
 * whatever `GET /deals/{id}/activity` returns for the caller; it doesn't
 * itself decide what a client is allowed to see.
 */
interface RecentActivityCardProps {
  activity: AnyRecord[];
  hostDealType?: string;
  limit?: number;
  /** Drop the "Recent activity" heading — the caller already labels the section
   *  (e.g. a <details> summary in the client portal). */
  bare?: boolean;
}

export default function RecentActivityCard({ activity, hostDealType, limit = 10, bare = false }: RecentActivityCardProps) {
  return (
    <div data-testid="portal-activity">
      {!bare && <div className="text-[12px] font-bold uppercase tracking-[1.2px] text-dash-muted mb-2">Recent activity</div>}
      <div className="bg-white border border-dash-border rounded-2xl p-5">
        <ActivityList
          items={activity as any}
          limit={limit}
          emptyTitle="No recent activity"
          emptyDescription="Updates from the deal team will appear here."
          className="gap-3"
          itemClassName="pb-3 border-b border-dash-border last:border-b-0 last:pb-0"
          renderMeta={(item: AnyRecord) => (
            <div className="mb-2 flex items-center gap-2">
              <RoleTagPill role={item.user_role} hostDealType={hostDealType} isInternalHost showSide={false} />
              <span className="text-[11px] text-dash-faint">
                {new Date(item.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
          )}
          renderMessage={(item: AnyRecord) => (
            <div className="text-[13px] leading-[1.38] text-dash-ink">
              <span className="font-medium">{item.user_name}</span> {item.message}
            </div>
          )}
        />
      </div>
    </div>
  );
}
